import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, FadeUp, ChapterLabel } from "@/components/Reveal";
import ValuesMarquee from "@/components/ValuesMarquee";
import BeforeAfter from "@/components/BeforeAfter";
import Testimonials from "@/components/Testimonials";
import { chantiers as chantiersFallback } from "@/data/chantiers";
import { services as servicesFallback } from "@/data/services";
import { useSiteData, resolveSrc, defaultSettings } from "@/api";

const HERO_IMG = "/chantiers/hero.jpg";

const engagements = [
  {
    n: "01",
    titre: "Rigueur",
    texte:
      "Chaque chantier est préparé dans les règles de l'art : supports contrôlés, protections posées, planning respecté.",
  },
  {
    n: "02",
    titre: "Soin du détail",
    texte:
      "Lignes nettes, raccords invisibles, arêtes propres. La différence se voit dans les finitions, pas dans les promesses.",
  },
  {
    n: "03",
    titre: "Ponctualité",
    texte:
      "Un délai annoncé est un délai tenu. Vous savez quand le chantier commence — et quand il se termine.",
  },
];

const Accueil = () => {
  const heroRef = useRef(null);
  const site = useSiteData({ chantiers: chantiersFallback, services: servicesFallback, settings: defaultSettings });
  const listeChantiers = site.chantiers || chantiersFallback;
  const listeServices = site.services || servicesFallback;
  const heroImg = resolveSrc(site.settings?.hero) || HERO_IMG;
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <>
      <Seo
        title="Alexis Peinture — Peintre en bâtiment à Angoulême & Cognac | Peinture intérieure décorative"
        description="Artisan peintre dans le Grand Angoulême et le Grand Cognac. Spécialiste de la peinture intérieure décorative : rigueur, finitions soignées, devis gratuit."
      />

      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-ink">
        <motion.img
          src={heroImg}
          alt="Pièce en fin de chantier de peinture, murs impeccables sous les spots"
          style={{ y: bgY, scale: 1.18 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-12 lg:pb-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="mb-8 text-xs uppercase tracking-[0.35em] text-sand"
            data-testid="hero-eyebrow"
          >
            Artisan peintre — Grand Angoulême &amp; Grand Cognac
          </motion.p>
          <h1
            className="max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            <MaskedLines
              lines={[
                "La peinture intérieure",
                <span key="l2">
                  <em className="italic text-sand">décorative</em>, avec exigence.
                </span>,
              ]}
              delay={0.55}
            />
          </h1>
          <FadeUp delay={1.2} className="mt-8 max-w-xl">
            <p className="text-base leading-relaxed text-cream/85 md:text-lg">
              Alexis Peinture transforme vos pièces à vivre avec un travail
              rigoureux et des finitions soignées. Du premier coup de rouleau
              au dernier raccord, chaque détail compte.
            </p>
          </FadeUp>
          <FadeUp delay={1.45} className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/contact"
              data-testid="hero-cta-devis"
              className="group flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-sand hover:text-ink"
            >
              Demander un devis gratuit
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/realisations"
              data-testid="hero-cta-realisations"
              className="flex items-center gap-2 border-b border-cream/40 pb-1 text-sm text-cream/90 transition-colors duration-300 hover:border-sand hover:text-sand"
            >
              Voir les réalisations <ArrowUpRight size={15} />
            </Link>
          </FadeUp>
        </div>
      </section>

      <ValuesMarquee />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-36" data-testid="home-services">
        <ChapterLabel number="01" label="Services" />
        <div className="mt-8 grid gap-16 lg:grid-cols-2">
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
              Une spécialité :<br />
              <em className="italic text-clay">l'intérieur décoratif.</em>
            </h2>
            <p className="mt-8 max-w-md leading-relaxed text-bark">
              Murs, plafonds, boiseries : Alexis Peinture se consacre à la
              peinture intérieure décorative, avec une exigence de finition
              constante. D'autres prestations complèteront bientôt l'offre.
            </p>
            <Link
              to="/services"
              data-testid="home-services-link"
              className="mt-10 inline-flex items-center gap-2 border-b border-ink pb-1 text-sm transition-colors duration-300 hover:border-clay hover:text-clay"
            >
              Découvrir tous les services <ArrowUpRight size={15} />
            </Link>
          </FadeUp>
          <div className="grid gap-5 sm:grid-cols-2">
            {listeServices.map((s, i) => (
              <FadeUp
                key={s.id}
                delay={0.15 + i * 0.08}
                className={i === 0 ? "sm:col-span-2" : ""}
              >
                <div
                  className={`group h-full p-8 transition-colors duration-500 ${
                    s.active ? "bg-ink text-cream" : "bg-plaster text-ink"
                  }`}
                  data-testid={`home-service-card-${s.id}`}
                >
                  <p className={`text-xs uppercase tracking-[0.25em] ${s.active ? "text-sand" : "text-taupe"}`}>
                    {s.active ? "Notre spécialité" : "Bientôt"}
                  </p>
                  <h3 className="mt-4 font-serif text-2xl tracking-tight">{s.titre}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${s.active ? "text-cream/75" : "text-bark"}`}>
                    {s.resume}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-plaster/60 py-24 lg:py-36" data-testid="home-realisations">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <ChapterLabel number="02" label="Réalisations" />
              <FadeUp delay={0.1}>
                <h2 className="mt-8 font-serif text-4xl tracking-tight sm:text-5xl">
                  Avant <em className="italic text-clay">/</em> Après
                </h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.2}>
              <Link
                to="/realisations"
                data-testid="home-gallery-link"
                className="inline-flex items-center gap-2 border-b border-ink pb-1 text-sm transition-colors duration-300 hover:border-clay hover:text-clay"
              >
                Toute la galerie <ArrowUpRight size={15} />
              </Link>
            </FadeUp>
          </div>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            {listeChantiers.slice(0, 3).map((c, i) => (
              <FadeUp key={c.id} delay={i * 0.12} className={i === 1 ? "md:translate-y-12" : ""}>
                {c.photo ? (
                  <div className="aspect-[4/3] w-full overflow-hidden bg-plaster">
                    <img
                      src={resolveSrc(c.photo)}
                      alt={c.titre}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1600ms] ease-out hover:scale-[1.04]"
                    />
                  </div>
                ) : (
                  <BeforeAfter image={c.apres || c.image} before={c.avant} alt={c.titre} testid={`home-before-after-${c.id}`} />
                )}
                <div className="mt-5 flex items-baseline justify-between">
                  <h3 className="font-serif text-xl tracking-tight">{c.titre}</h3>
                  {c.ville && <p className="text-xs uppercase tracking-[0.2em] text-taupe">{c.ville}</p>}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 text-cream lg:py-36" data-testid="home-engagements">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <ChapterLabel number="03" label="Engagements" light />
          <FadeUp delay={0.1}>
            <h2 className="mt-8 max-w-2xl font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
              Ce qui fait la différence, <em className="italic text-sand">chantier après chantier.</em>
            </h2>
          </FadeUp>
          <div className="mt-20 grid gap-12 md:grid-cols-3">
            {engagements.map((e, i) => (
              <FadeUp key={e.n} delay={i * 0.12}>
                <p className="font-serif text-5xl italic text-clay">{e.n}</p>
                <h3 className="mt-5 font-serif text-2xl tracking-tight">{e.titre}</h3>
                <p className="mt-4 text-sm leading-relaxed text-cream/70">{e.texte}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <section className="py-28 lg:py-40" data-testid="home-cta">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-12">
          <FadeUp>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Un projet de peinture <em className="italic text-clay">en tête ?</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mx-auto mt-8 max-w-xl leading-relaxed text-bark">
              Décrivez votre chantier, joignez quelques photos, et recevez un
              devis clair et gratuit. Alexis vous répond personnellement.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <Link
              to="/contact"
              data-testid="home-cta-devis"
              className="group mt-12 inline-flex items-center gap-3 rounded-full bg-clay px-10 py-5 text-sm font-medium text-cream transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-ink"
            >
              Demander mon devis gratuit
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Accueil;
